// shared/src/oracle/dash-bundle.ts
// The Ops-dash Oracle read contract (design doc §9) — ONE bundle powers all four
// surfaces (Params/config · Runs · Cost/quota · Health) plus the raw-vs-resolved
// compare + provenance. Defined ONCE here so the api-worker projector and the
// apps/web tab share it verbatim (no C035 hand-mirror, no widening cast).
//
// The projector (api-worker db/ layer) runs the SQL, then calls the PURE
// assembler below — so the shaping logic is unit-testable in shared/ with no
// Worker/D1 dependency. Rows in → validated bundle out.

import { z } from 'zod';
import type {
  OracleSourceRow, OraclePolicyRow, OracleObservationRow, OracleCanonicalRow,
} from './oracle-store.js';
import type { UsedSource, RejectedSource } from './resolve-consensus.js';
import { oracleSourceProvenance, explorerUrlForPoolRef } from './source-provenance.js';

// ─────────────────────────── view schemas ───────────────────────────

/** ONE oracle_policy row (one asset_class), surfaced for the Params tab.
 *
 *  `asset_class` + `mode` + `corroboration_tol_pct` are NOT decoration. The view used to carry the
 *  four consensus knobs only, and said "verbatim" while dropping these three — harmless while every
 *  class shared one CONSENSUS row, and actively misleading the moment migration 0100 gave equity an
 *  AUTHORITATIVE row: the tab would render min_sources/mad_k (inert under AUTHORITATIVE) as though
 *  they governed equity's price, with nothing on screen saying otherwise.
 *
 *  The web parses the bundle with THIS schema (apps/web/src/data/queries/oracle.ts), so a field
 *  absent here is stripped before the UI sees it — the C035 guard doing its job. Widening the view
 *  is therefore the only way the UI can show the truth. */
export const OraclePolicyViewSchema = z.object({
  data_type:             z.literal('price'),
  asset_class:           z.enum(['*', 'crypto', 'equity']),
  mode:                  z.enum(['consensus', 'authoritative']),
  min_sources:           z.number(),
  mad_k:                 z.number(),
  outlier_floor_pct:     z.number(),
  freshness_half_life_s: z.number(),
  corroboration_tol_pct: z.number().nullable(),
  policy_version:        z.string(),
  updated_at:            z.string(),
  updated_by:            z.string().nullable(),
});

/** oracle_sources config row + derived Health/Cost columns (last N-h window). */
export const OracleSourceViewSchema = z.object({
  // config (Params tab, editable)
  source_id:       z.string(),
  enabled:         z.boolean(),
  weight:          z.number(),
  tier:            z.enum(['A', 'B', 'C']),
  poll_interval_s: z.number(),
  staleness_max_s: z.number(),
  config_json:     z.string().nullable(),
  updated_at:      z.string(),
  updated_by:      z.string().nullable(),
  // Provenance (#110, from ORACLE_SOURCE_PROVENANCE — display metadata, not editable):
  // the on-chain pool this source reads (Uniswap sources only) + its block-explorer URL, and
  // the Secrets-Store credential binding it authenticates with (null = keyless/public).
  pool_ref:        z.string().nullable(),   // v3 pool address · v4 bytes32 poolId · null
  pool_url:        z.string().nullable(),   // explorer link for pool_ref · null
  credential:      z.string().nullable(),   // e.g. 'ALCHEMY_APP_API_KEY' · null = public
  // Health (derived from recent observations)
  last_observed_at: z.string().nullable(),
  last_fetched_at:  z.string().nullable(),
  last_value:       z.number().nullable(),
  last_status:      z.enum(['ok', 'stale', 'error', 'none']),
  age_s:            z.number().nullable(),   // now − last_observed_at
  // Cost / quota (call counts over the window)
  obs_count:        z.number(),
  error_count:      z.number(),
  ratelimit_count:  z.number(),
});

const UsedSourceViewSchema = z.object({
  source_id: z.string(),
  value:     z.number(),
  weight:    z.number(),
  age_s:     z.number(),
});
const RejectedSourceViewSchema = z.object({
  source_id: z.string(),
  value:     z.number().nullable(),
  reason:    z.enum(['error', 'no_value', 'stale', 'unconfigured', 'outlier', 'disagree']),
  delta:     z.number().optional(),
});

/** One raw candidate beside the resolved value (raw-vs-resolved compare). */
export const OracleObservationViewSchema = z.object({
  source_id:    z.string(),
  value:        z.number().nullable(),
  observed_at:  z.string().nullable(),
  fetched_at:   z.string(),
  age_s:        z.number().nullable(),
  quality:      z.array(z.string()),
  disposition:  z.enum(['used', 'rejected', 'unknown']),
  reject_reason: z.enum(['error', 'no_value', 'stale', 'unconfigured', 'outlier', 'disagree']).nullable(),
  delta:        z.number().nullable(),
});

/** Per canonical key ($ETH / $BTC): the resolved value + full provenance + the
 *  raw candidates from the SAME run (the compare view). */
export const OracleKeyViewSchema = z.object({
  key:            z.string(),
  value:          z.number().nullable(),
  prev_value:     z.number().nullable(),   // previous run's resolved value → Δ vs prev run
  observed_at:    z.string().nullable(),
  status:         z.enum(['ok', 'below_quorum', 'no_candidates']),
  run_id:         z.string(),
  created_at:     z.string(),
  policy_version: z.string(),
  median:         z.number().nullable(),
  spread:         z.number().nullable(),
  mad:            z.number().nullable(),   // weighted MAD of the pack (consensus only; null under authoritative)
  threshold:      z.number().nullable(),   // outlier cutoff actually used = max(mad_k·mad, floor); null under authoritative
  used:           z.array(UsedSourceViewSchema),
  rejected:       z.array(RejectedSourceViewSchema),
  observations:   z.array(OracleObservationViewSchema),
  provenance_error: z.boolean(),   // true ⇒ provenance_json failed to parse (visible, not silent)
  observations_from_prior_run: z.boolean(), // true ⇒ this run wrote no obs for the key (poll-gate skip);
                                            // the drilldown shows the latest obs PER SOURCE from an earlier
                                            // run so the sources stay visible instead of blank (age_s reveals staleness)
  in_universe: z.boolean(),   // true ⇒ the key is in the oracle's CURRENTLY-MAINTAINED universe
                              // (effectiveUniverse ∪ holdings). false ⇒ a DE-LISTED key whose canonical row
                              // is frozen last-good; the dash renders it in a dimmed "de-listed" tail, never
                              // in the live baskets (so a dropped ticker reads as retired, not silently gone).
});

/** One base-tick ingest+resolve cycle (Runs tab), keyed by run_id. */
export const OracleRunViewSchema = z.object({
  run_id:     z.string(),
  created_at: z.string(),
  keys: z.array(z.object({
    key:            z.string(),
    status:         z.enum(['ok', 'below_quorum', 'no_candidates']),
    value:          z.number().nullable(),
    used_count:     z.number(),
    rejected_count: z.number(),
  })),
});

export const OracleDashBundleSchema = z.object({
  generated_at: z.string(),
  data_type:    z.literal('price'),
  window_h:     z.number(),          // the Health/Cost aggregation window
  // EVERY price policy row, general ('*') first then classes. Replaces the old singular `policy`,
  // which could only ever be the '*' row: after migration 0100 that row is crypto's, so rendering it
  // as "the price policy" would state equity's resolver wrongly rather than incompletely. An empty
  // array ⇒ no rows at all ⇒ resolver on the in-code default (visible, same meaning the old
  // `null` carried).
  policies:     z.array(OraclePolicyViewSchema),
  sources:      z.array(OracleSourceViewSchema),
  keys:         z.array(OracleKeyViewSchema),
  runs:         z.array(OracleRunViewSchema),
});

export type OraclePolicyView      = z.infer<typeof OraclePolicyViewSchema>;
export type OracleSourceView      = z.infer<typeof OracleSourceViewSchema>;
export type OracleObservationView = z.infer<typeof OracleObservationViewSchema>;
export type OracleKeyView         = z.infer<typeof OracleKeyViewSchema>;
export type OracleRunView             = z.infer<typeof OracleRunViewSchema>;
export type OracleDashBundle          = z.infer<typeof OracleDashBundleSchema>;

// ─────────────────────────── pure assembler ───────────────────────────

/** Provenance persisted in oracle_canonical.provenance_json (worker writes this
 *  exact shape: scheduled.ts → JSON.stringify({used, rejected, median, spread, mad, threshold})).
 *  Exported so the oracle-worker round-trip test can assert the persisted JSON parses. */
export const ProvenanceSchema = z.object({
  used:     z.array(UsedSourceViewSchema).default([]),
  rejected: z.array(RejectedSourceViewSchema).default([]),
  median:   z.number().nullable().default(null),
  spread:   z.number().nullable().default(null),
  mad:      z.number().nullable().default(null),       // weighted MAD of the pack (persisted for the Consensus tab)
  threshold: z.number().nullable().default(null),      // outlier cutoff actually used = max(mad_k·mad, floor)
});

/** Per-source aggregate over the Health/Cost window, derived internally from the
 *  window observations (kept as a named shape for the sourceStatus helper). */
interface SourceHealthAgg {
  readonly obs_count:        number;
  readonly error_count:      number;
  readonly ratelimit_count:  number;
  readonly last_observed_at: string | null;
  readonly last_fetched_at:  string | null;
  readonly last_value:       number | null;
  readonly last_quality:     ReadonlyArray<string>; // quality[] of the most-recent obs
}

export interface AssembleInput {
  readonly nowMs:        number;
  readonly generatedAt:  string;   // ISO-Z
  readonly windowH:      number;
  readonly policies:     ReadonlyArray<OraclePolicyRow>;
  readonly sources:      ReadonlyArray<OracleSourceRow>;
  /** ALL observations in the Health/Cost window (newest-or-any order). The
   *  assembler derives the per-source health aggregate AND the raw-vs-resolved
   *  compare (filtering to each latest canonical's run) from this one set. */
  readonly windowObservations: ReadonlyArray<OracleObservationRow>;
  /** Recent canonical rows (all keys) for the Runs list (a last-N-rows window). */
  readonly recentCanonical: ReadonlyArray<OracleCanonicalRow>;
  /** Latest canonical PER KEY (one row per key, whole universe) — the CONSENSUS
   *  keys view. Independent of the Runs window so a fast-cadence class never hides
   *  slower/dormant keys. Falls back to recentCanonical when not supplied (legacy). */
  readonly latestCanonical?: ReadonlyArray<OracleCanonicalRow>;
  /** Previous resolved value per key (2nd-newest canonical) → the Δ-vs-prev-run column. */
  readonly prevValueByKey?: ReadonlyMap<string, number | null>;
  /** The oracle's currently-maintained key universe (canonical `$`-key strings =
   *  effectiveUniverse ∪ holdings). A key present here is `in_universe: true`; any
   *  latest-canonical key NOT in this set is a DE-LISTED key (its row is frozen
   *  last-good). OPTIONAL: when omitted (legacy callers), every key defaults
   *  `in_universe: true` — the pre-de-listing behaviour, unchanged. */
  readonly universe?: ReadonlyArray<string>;
}

const RATELIMIT_RE = /429|rate.?limit/i;

/** Fold the window observations into a per-source aggregate (latest obs by
 *  fetched_at wins for the "last_*" fields). Pure. */
function aggregateHealth(obs: ReadonlyArray<OracleObservationRow>): Map<string, SourceHealthAgg> {
  const acc = new Map<string, {
    obs_count: number; error_count: number; ratelimit_count: number;
    last: OracleObservationRow | null; lastQuality: string[];
  }>();
  for (const o of obs) {
    let a = acc.get(o.source_id);
    if (!a) { a = { obs_count: 0, error_count: 0, ratelimit_count: 0, last: null, lastQuality: [] }; acc.set(o.source_id, a); }
    a.obs_count += 1;
    const q = parseQuality(o.quality);
    if (o.value == null || q.some((x) => x.startsWith('error:'))) a.error_count += 1;
    if (q.some((x) => RATELIMIT_RE.test(x))) a.ratelimit_count += 1;
    if (!a.last || o.fetched_at > a.last.fetched_at) { a.last = o; a.lastQuality = q; }
  }
  const out = new Map<string, SourceHealthAgg>();
  for (const [id, a] of acc) {
    out.set(id, {
      obs_count: a.obs_count, error_count: a.error_count, ratelimit_count: a.ratelimit_count,
      last_observed_at: a.last?.observed_at ?? null, last_fetched_at: a.last?.fetched_at ?? null,
      last_value: a.last?.value ?? null, last_quality: a.lastQuality,
    });
  }
  return out;
}

function parseQuality(raw: string): string[] {
  try {
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v.map(String) : [];
  } catch {
    return [];
  }
}

function ageSeconds(observedAt: string | null, nowMs: number): number | null {
  if (!observedAt) return null;
  const ms = Date.parse(observedAt);
  return Number.isFinite(ms) ? Math.round((nowMs - ms) / 1000) : null;
}

/** Health status of a source's most-recent observation. */
function sourceStatus(
  agg: SourceHealthAgg | undefined, staleMaxS: number, nowMs: number,
): { status: OracleSourceView['last_status']; age_s: number | null } {
  if (!agg || agg.obs_count === 0) return { status: 'none', age_s: null };
  if (agg.last_quality.some((q) => q.startsWith('error:')) || agg.last_value == null) {
    return { status: 'error', age_s: ageSeconds(agg.last_observed_at, nowMs) };
  }
  const age = ageSeconds(agg.last_observed_at, nowMs);
  if (age == null || age > staleMaxS) return { status: 'stale', age_s: age };
  return { status: 'ok', age_s: age };
}

/**
 * PURE: already-fetched D1 rows → the validated Ops-dash bundle. No I/O. The
 * api-worker projector supplies the rows; this shapes + validates them.
 */
export function assembleOracleDashBundle(input: AssembleInput): OracleDashBundle {
  const { nowMs } = input;
  const healthById = aggregateHealth(input.windowObservations);

  // ── sources (config + health/cost) ──
  const sources: OracleSourceView[] = input.sources.map((s) => {
    const agg = healthById.get(s.source_id);
    const { status, age_s } = sourceStatus(agg, s.staleness_max_s, nowMs);
    const prov = oracleSourceProvenance(s.source_id);
    return {
      source_id:       s.source_id,
      enabled:         s.enabled === 1,
      weight:          s.weight,
      tier:            s.tier,
      poll_interval_s: s.poll_interval_s,
      staleness_max_s: s.staleness_max_s,
      config_json:     s.config_json,
      updated_at:      s.updated_at,
      updated_by:      s.updated_by,
      pool_ref:        prov?.poolRef?.value ?? null,
      pool_url:        explorerUrlForPoolRef(prov?.poolRef),
      credential:      prov?.credential ?? null,
      last_observed_at: agg?.last_observed_at ?? null,
      last_fetched_at:  agg?.last_fetched_at ?? null,
      last_value:       agg?.last_value ?? null,
      last_status:      status,
      age_s,
      obs_count:        agg?.obs_count ?? 0,
      error_count:      agg?.error_count ?? 0,
      ratelimit_count:  agg?.ratelimit_count ?? 0,
    };
  });

  // ── latest canonical per key (from the dedicated per-key read when supplied,
  //    else the Runs window; still fold to newest-per-key in case of ties) ──
  const latestByKey = new Map<string, OracleCanonicalRow>();
  for (const c of input.latestCanonical ?? input.recentCanonical) {
    const prev = latestByKey.get(c.key);
    if (!prev || c.created_at > prev.created_at) latestByKey.set(c.key, c);
  }
  // Membership set for the in_universe flag. Absent (legacy caller) ⇒ null ⇒ every key
  // defaults in_universe:true (pre-de-listing behaviour). Present ⇒ a key not in the set
  // is DE-LISTED (frozen last-good canonical).
  const universeSet = input.universe ? new Set(input.universe) : null;

  // observations grouped by (run_id, key) for the exact-run compare view, and by
  // key alone for the poll-gate-skip fallback below. On a fire where a key's own
  // sources were all poll-gated off, NO observation rows are written for that run,
  // yet a canonical row is still emitted carrying last-good forward (its used[]
  // still lists the sources). The exact-run join then returns [] → the drilldown
  // read "no source observations" while the summary showed used sources — a
  // display-only contradiction. The by-key index lets us fall back to the latest
  // observation per source for the key so the sources stay visible.
  const obsByRunKey = new Map<string, OracleObservationRow[]>();
  const obsByKey = new Map<string, OracleObservationRow[]>();
  for (const o of input.windowObservations) {
    const k = `${o.run_id} ${o.key}`;
    (obsByRunKey.get(k) ?? obsByRunKey.set(k, []).get(k)!).push(o);
    (obsByKey.get(o.key) ?? obsByKey.set(o.key, []).get(o.key)!).push(o);
  }

  const keys: OracleKeyView[] = [...latestByKey.values()]
    .sort((a, b) => a.key.localeCompare(b.key))
    .map((c) => {
      let used: UsedSource[] = [];
      let rejected: RejectedSource[] = [];
      let median: number | null = null;
      let spread: number | null = null;
      let mad: number | null = null;
      let threshold: number | null = null;
      let provenance_error = false;
      const parsed = ProvenanceSchema.safeParse(safeJson(c.provenance_json));
      if (parsed.success) {
        used = parsed.data.used as UsedSource[];
        rejected = parsed.data.rejected as RejectedSource[];
        median = parsed.data.median;
        spread = parsed.data.spread;
        mad = parsed.data.mad;
        threshold = parsed.data.threshold;
      } else {
        provenance_error = true; // visible, not silent
      }

      const usedById = new Map(used.map((u) => [u.source_id, u]));
      const rejById = new Map(rejected.map((r) => [r.source_id, r]));
      // Exact-run observations for the compare view. When this run wrote none for
      // the key (all its sources poll-gated off, canonical carried last-good), fall
      // back to the latest observation PER SOURCE for the key across the window so
      // the drilldown reflects the real sources instead of a blank that contradicts
      // used[]. Flagged (observations_from_prior_run) — never silent.
      let obs = obsByRunKey.get(`${c.run_id} ${c.key}`) ?? [];
      let observations_from_prior_run = false;
      if (obs.length === 0) {
        const latestPerSource = new Map<string, OracleObservationRow>();
        for (const o of obsByKey.get(c.key) ?? []) {
          const prev = latestPerSource.get(o.source_id);
          if (!prev || o.created_at > prev.created_at) latestPerSource.set(o.source_id, o);
        }
        if (latestPerSource.size > 0) {
          obs = [...latestPerSource.values()];
          observations_from_prior_run = true;
        }
      }
      const observations: OracleObservationView[] = obs
        .slice()
        .sort((a, b) => a.source_id.localeCompare(b.source_id))
        .map((o) => {
          const rej = rejById.get(o.source_id);
          const disposition: OracleObservationView['disposition'] =
            usedById.has(o.source_id) ? 'used' : rej ? 'rejected' : 'unknown';
          return {
            source_id:     o.source_id,
            value:         o.value,
            observed_at:   o.observed_at,
            fetched_at:    o.fetched_at,
            age_s:         ageSeconds(o.observed_at, nowMs),
            quality:       parseQuality(o.quality),
            disposition,
            reject_reason: rej?.reason ?? null,
            delta:         rej?.delta ?? null,
          };
        });

      return {
        key:            c.key,
        value:          c.value,
        prev_value:     input.prevValueByKey?.get(c.key) ?? null,
        observed_at:    c.observed_at,
        status:         c.status,
        run_id:         c.run_id,
        created_at:     c.created_at,
        policy_version: c.policy_version,
        median,
        spread,
        mad,
        threshold,
        used:           used.map((u) => ({ source_id: u.source_id, value: u.value, weight: u.weight, age_s: u.age_s })),
        rejected:       rejected.map((r) => ({ source_id: r.source_id, value: r.value ?? null, reason: r.reason, ...(r.delta != null ? { delta: r.delta } : {}) })),
        observations,
        provenance_error,
        observations_from_prior_run,
        in_universe: universeSet ? universeSet.has(c.key) : true,
      };
    });

  // ── runs (recent cycles), grouped by run_id ──
  const runMap = new Map<string, OracleRunView>();
  for (const c of input.recentCanonical) {
    let run = runMap.get(c.run_id);
    if (!run) {
      run = { run_id: c.run_id, created_at: c.created_at, keys: [] };
      runMap.set(c.run_id, run);
    }
    if (c.created_at > run.created_at) (run as { created_at: string }).created_at = c.created_at;
    const prov = ProvenanceSchema.safeParse(safeJson(c.provenance_json));
    run.keys.push({
      key:            c.key,
      status:         c.status,
      value:          c.value,
      used_count:     prov.success ? prov.data.used.length : 0,
      rejected_count: prov.success ? prov.data.rejected.length : 0,
    });
  }
  const runs = [...runMap.values()]
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
    .map((r) => ({ ...r, keys: r.keys.slice().sort((a, b) => a.key.localeCompare(b.key)) }));

  const bundle: OracleDashBundle = {
    generated_at: input.generatedAt,
    data_type:    'price',
    window_h:     input.windowH,
    // data_type is pinned to the literal 'price' rather than copied from the row: this bundle IS the
    // price bundle (see `data_type` above), macro has its own. The reader already filters to price,
    // so a row of any other type here would be a bug upstream, not something to render.
    policies: input.policies.map((p) => ({
      data_type:             'price' as const,
      asset_class:           p.asset_class,
      mode:                  p.mode,
      min_sources:           p.min_sources,
      mad_k:                 p.mad_k,
      outlier_floor_pct:     p.outlier_floor_pct,
      freshness_half_life_s: p.freshness_half_life_s,
      corroboration_tol_pct: p.corroboration_tol_pct,
      policy_version:        p.policy_version,
      updated_at:            p.updated_at,
      updated_by:            p.updated_by,
    })),
    sources,
    keys,
    runs,
  };
  return OracleDashBundleSchema.parse(bundle);
}

function safeJson(raw: string): unknown {
  try { return JSON.parse(raw); } catch { return null; }
}
